import { memo, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, type CreativeBudgetRecord, type CreativeEvent, type CreativeSessionStatus } from '../../../services/api';
import { ActivityIcon, formatCredits, formatDuration, MarkdownText } from './creativeUi';
import { SlideStrip } from './DeliverableViewer';
import { PublicationCard } from './PublicationCard';
import { IdeasCard, type IdeaItem } from './IdeasCard';

// Linha do tempo da sessão: mensagens, blocos de trabalho (atividades seguidas agrupadas),
// plano, orçamento, avisos e erros.

type Item =
  | { kind: 'event'; event: CreativeEvent }
  | { kind: 'work'; id: string; activities: CreativeEvent[] };

function groupEvents(events: CreativeEvent[]): Item[] {
  const items: Item[] = [];
  for (const ev of events) {
    if (ev.type === 'activity') {
      const last = items[items.length - 1];
      if (last && last.kind === 'work') last.activities.push(ev);
      else items.push({ kind: 'work', id: `w${ev.id}`, activities: [ev] });
    } else {
      items.push({ kind: 'event', event: ev });
    }
  }
  return items;
}

// Relógio para as durações de atividades em andamento.
function useTick(active: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

const FORMAT_BADGE: Record<string, string> = {
  reel: 'Reel', carousel: 'Carrossel', post: 'Post', tiktok: 'TikTok', tiktok_carousel: 'Carrossel TT', video: 'Vídeo', image: 'Imagem',
};

export function compactNumber(n: any): string {
  const v = Number(n || 0);
  return v >= 1000 ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : String(Math.round(v));
}

// Miniatura de post de referência (atividades de pesquisa e Biblioteca).
export function ReferenceThumb({ post, onClick }: { post: any; onClick?: () => void }) {
  const m = post.metrics || {};
  const Wrapper: any = onClick ? 'button' : 'a';
  const props = onClick ? { type: 'button', onClick } : { href: post.url, target: '_blank', rel: 'noopener noreferrer' };
  return (
    <Wrapper {...props} className="group relative block w-28 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left">
      {post.thumbnailUrl
        ? <img src={post.thumbnailUrl} alt="" loading="lazy" className="aspect-[4/5] w-full object-cover transition group-hover:scale-[1.03]" onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
        : <div className="aspect-[4/5] w-full" />}
      <span className="absolute left-1 top-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">{FORMAT_BADGE[post.format] || post.format || 'post'}</span>
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-1.5 pb-1 pt-4 text-[10px] font-semibold text-white">
        {m.views ? `▶ ${compactNumber(m.views)} · ` : ''}♥ {compactNumber(m.likes)}
      </span>
    </Wrapper>
  );
}

// Desempenho (celular): o polling re-renderizava a linha do tempo inteira a cada 1,5 s. Os
// eventos que não mudaram mantêm a mesma referência (CreateContentAIView só troca os que
// voltam do servidor), então memo por identidade evita re-render e re-parse de markdown.
const ActivityRow = memo(function ActivityRow({ ev, onImage, onOpenDeliverable }: { ev: CreativeEvent; onImage: (url: string) => void; onOpenDeliverable: (id: string, index: number) => void }) {
  const p = ev.payload || {};
  const running = p.status === 'running';
  const failed = p.status === 'error';
  return (
    <div className="flex items-start gap-3 py-2">
      <ActivityIcon kind={p.kind} running={running} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`text-sm font-semibold ${failed ? 'text-rose-700' : 'text-slate-800'}`}>{p.label}</span>
          {running && <span className="text-xs font-medium text-indigo-600">em andamento…</span>}
          {!running && p.summary && <span className={`text-xs ${failed ? 'text-rose-600' : 'text-slate-500'}`}>· {p.summary}</span>}
        </div>
        {p.detail && <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">{p.detail}</p>}
        {p.data?.deliverable?.slides?.length > 0 && (
          <div className="mt-2">
            <SlideStrip slides={p.data.deliverable.slides} coverIndex={p.data.deliverable.coverIndex} onOpen={i => onOpenDeliverable(p.data.deliverable.id, i)} />
          </div>
        )}
        {Array.isArray(p.data?.posts) && p.data.posts.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {p.data.posts.map((post: any) => <ReferenceThumb key={post.id} post={post} />)}
          </div>
        )}
        {Array.isArray(p.data?.sources) && p.data.sources.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.data.sources.slice(0, 6).map((u: string) => {
              let host = u;
              try { host = new URL(u).hostname.replace(/^www\./, ''); } catch { /* mantém a URL */ }
              return <a key={u} href={u} target="_blank" rel="noopener noreferrer" className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700 hover:bg-sky-100">{host}</a>;
            })}
          </div>
        )}
        {Array.isArray(p.data?.audios) && p.data.audios.length > 0 && (
          <div className="mt-2 space-y-1">
            {p.data.audios.map((a: any) => <audio key={a.url} src={a.url} controls preload="none" className="h-9 w-full max-w-md" />)}
          </div>
        )}
        {Array.isArray(p.data?.videos) && p.data.videos.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {p.data.videos.map((v: any) => (
              // preload="none": dezenas de vídeos na conversa baixavam todos ao abrir.
              <video key={v.url} src={v.url} controls playsInline preload="none" className="h-64 w-auto max-w-full rounded-xl border border-slate-200 bg-black" />
            ))}
          </div>
        )}
        {Array.isArray(p.data?.images) && p.data.images.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {p.data.images.map((img: any) => (
              <button key={img.url} type="button" onClick={() => onImage(img.url)} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <img src={img.url} alt="" loading="lazy" decoding="async" className="h-40 w-auto max-w-full object-cover transition group-hover:scale-[1.02]" />
                {Array.isArray(img.issues) && img.issues.some((i: any) => i.severity === 'error') && <span className="absolute left-1 top-1 rounded-md bg-rose-500 px-1.5 py-0.5 text-[9px] font-bold text-white">{img.issues.filter((i: any) => i.severity === 'error').length} erro(s)</span>}
                <span className="absolute bottom-1 right-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">{img.size}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5 pt-0.5 text-[11px] text-slate-400">
        <span className="flex items-center gap-1">
          {running
            ? <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
            : failed
              ? <svg className="h-3.5 w-3.5 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path d="M6 6l12 12M18 6 6 18" /></svg>
              : <svg className="h-3.5 w-3.5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path d="m5 12 5 5L20 7" /></svg>}
          {formatDuration(p.startedAt, p.endedAt)}
        </span>
        {Number(p.credits) > 0 && <span className="font-semibold text-emerald-600">{formatCredits(p.credits, 3)} cr</span>}
      </div>
    </div>
  );
});

// Blocos antigos nascem FECHADOS: só o último (ou o que está rodando) abre sozinho. Antes todos
// abriam, e uma conversa longa montava centenas de linhas, miniaturas e vídeos de uma vez.
const WorkBlock = memo(function WorkBlock({ activities, live, defaultOpen, onImage, onOpenDeliverable }: { activities: CreativeEvent[]; live: boolean; defaultOpen: boolean; onImage: (url: string) => void; onOpenDeliverable: (id: string, index: number) => void }) {
  const running = activities.some(a => a.payload?.status === 'running');
  const [open, setOpen] = useState(defaultOpen || running);
  useEffect(() => { if (running) setOpen(true); }, [running]);
  useTick(running);
  const done = activities.filter(a => a.payload?.status === 'done').length;
  const failed = activities.filter(a => a.payload?.status === 'error').length;
  const current = activities.find(a => a.payload?.status === 'running');
  const credits = activities.reduce((s, a) => s + Number(a.payload?.credits || 0), 0);
  return (
    <div className={`rounded-2xl border bg-white shadow-sm ${running ? 'border-indigo-200 ring-2 ring-indigo-50' : 'border-slate-200'}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <span className={`flex h-2.5 w-2.5 rounded-full ${running ? 'bg-indigo-500 animate-pulse' : failed ? 'bg-amber-500' : 'bg-emerald-500'}`} />
        <span className="flex-1 text-sm font-semibold text-slate-700">
          {running ? (current?.payload?.label || 'Trabalhando') : `${done} etapa${done === 1 ? '' : 's'} concluída${done === 1 ? '' : 's'}${failed ? ` · ${failed} com erro` : ''}`}
        </span>
        {credits > 0 && <span className="text-xs font-semibold text-emerald-600">{formatCredits(credits, 3)} cr</span>}
        <svg className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="divide-y divide-slate-100 border-t border-slate-100 px-4">
          {activities.map(a => <ActivityRow key={a.id} ev={a} onImage={onImage} onOpenDeliverable={onOpenDeliverable} />)}
          {live && !running && <div className="py-2 text-xs text-slate-400">pensando no próximo passo…</div>}
        </div>
      )}
    </div>
  );
}, (a, b) => a.live === b.live && a.defaultOpen === b.defaultOpen && a.onImage === b.onImage && a.onOpenDeliverable === b.onOpenDeliverable
  && a.activities.length === b.activities.length && a.activities.every((ev, i) => ev === b.activities[i]));

const FORMAT_LABELS: Record<string, string> = {
  direcao: 'Cartão de direção', carrossel: 'Carrossel', post: 'Post estático', reel_avatar: 'Reel com avatar', reel_motion: 'Reel com motion', outro: 'Conteúdo',
};

function PlanCard({ p }: { p: any }) {
  const hook = p.hook || {};
  const totalS = (p.steps || []).reduce((s: number, st: any) => s + (Number(st.duration_s) || 0), 0);
  return (
    <div className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-violet-100 bg-violet-50/60 px-5 py-3">
        <ActivityIcon kind="plan" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-violet-500">{p.format === 'direcao' ? 'Direção' : `Plano · ${FORMAT_LABELS[p.format] || p.format}`}{totalS ? ` · ${totalS}s` : ''}</p>
          <p className="truncate text-base font-bold text-slate-900">{p.title}</p>
        </div>
      </div>
      <div className="space-y-4 px-5 py-4">
        {p.angle && <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Ângulo: </span>{p.angle}</p>}
        {(hook.fala || hook.visual || hook.texto) && (
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Gancho</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {[['Fala', hook.fala], ['Visual', hook.visual], ['Texto na tela', hook.texto]].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500">{k}</p>
                  <p className="mt-0.5 text-sm text-slate-700">{v || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {Array.isArray(p.steps) && p.steps.length > 0 && (
          <ol className="space-y-2">
            {p.steps.map((st: any, i: number) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">{st.title}{st.duration_s ? <span className="ml-2 text-xs font-medium text-slate-400">{st.duration_s}s</span> : null}</p>
                  <p className="text-sm text-slate-600">{st.content}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
        {(p.tone || p.notes) && (
          <p className="text-xs text-slate-500">{p.tone ? <><span className="font-semibold">Tom:</span> {p.tone}. </> : null}{p.notes}</p>
        )}
      </div>
    </div>
  );
}

// Orçamento com duas versões (reforma definitiva): a RECOMENDADA é o pacote completo que a direção
// pede; a ECONÔMICA, quando o agente oferece, aparece ao lado com o que ela perde. O usuário escolhe.
function BudgetLines({ lines }: { lines: any[] }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {lines.map((l: any, i: number) => (
          <tr key={i} className="border-b border-slate-50 last:border-0">
            <td className="py-1.5 pr-2">
              <p className="font-medium text-slate-800">{l.label}</p>
              {l.note && <p className="text-xs text-slate-400">{l.note}</p>}
            </td>
            <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs text-slate-500">
              {l.unit === '1M tokens' ? `${Math.round(l.qty * 1e6).toLocaleString('pt-BR')} tokens` : `${Number(l.qty).toLocaleString('pt-BR')} ${l.unit}`}
            </td>
            <td className="whitespace-nowrap py-1.5 pl-2 text-right font-mono text-slate-700">{formatCredits(l.credits, 3)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BudgetCard({ p, budget, canDecide, onDecide }: {
  p: any;
  budget: CreativeBudgetRecord | undefined;
  canDecide: boolean;
  onDecide: (budgetId: string, decision: 'approve' | 'reject', reason?: string, option?: 'recomendado' | 'economico') => Promise<void>;
}) {
  const [busy, setBusy] = useState<'approve' | 'economy' | 'reject' | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [tab, setTab] = useState<'recomendado' | 'economico'>('recomendado');
  const status = budget?.status || p.status;
  const economy = p.economy && Array.isArray(p.economy.lines) && p.economy.lines.length ? p.economy : null;
  const chosen: 'recomendado' | 'economico' = p.option === 'economico' ? 'economico' : 'recomendado';
  // Antes da decisão, a aba escolhida; depois, a versão aprovada (as linhas do orçamento já são dela).
  const showing = status === 'proposed' ? tab : chosen;
  const lines = status === 'proposed'
    ? (showing === 'economico' && economy ? economy.lines : p.lines || [])
    : (budget?.lines || p.lines || []);
  const total = status === 'proposed'
    ? (showing === 'economico' && economy ? economy.totalCredits : p.totalCredits)
    : (budget?.totalCredits ?? p.totalCredits);
  const recTotal = p.totalCredits;
  const decide = async (d: 'approve' | 'reject', option?: 'recomendado' | 'economico') => {
    setBusy(d === 'reject' ? 'reject' : option === 'economico' ? 'economy' : 'approve');
    try { await onDecide(p.budgetId, d, d === 'reject' ? reason.trim() || undefined : undefined, option); setRejecting(false); } finally { setBusy(null); }
  };
  const statusBadge: Record<string, string> = {
    approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-rose-100 text-rose-700', superseded: 'bg-slate-100 text-slate-500', proposed: 'bg-amber-100 text-amber-800',
  };
  const statusLabel: Record<string, string> = { approved: chosen === 'economico' ? 'Aprovado (econômico)' : 'Aprovado', rejected: 'Recusado', superseded: 'Substituído', proposed: 'Aguardando você' };
  return (
    <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${status === 'proposed' ? 'border-amber-300 ring-2 ring-amber-50' : 'border-slate-200'}`}>
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
        <ActivityIcon kind="budget" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Orçamento{economy ? (showing === 'economico' ? ' · Econômico' : ' · Recomendado') : ''}</p>
          <p className="text-base font-bold text-slate-900">{formatCredits(total, 3)} créditos</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadge[status] || statusBadge.proposed}`}>{statusLabel[status] || status}</span>
      </div>
      <div className="px-5 py-3">
        {economy && status === 'proposed' && (
          <div className="mb-3 grid grid-cols-2 gap-2" role="tablist">
            {(['recomendado', 'economico'] as const).map(k => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                className={`rounded-xl border px-3 py-2 text-left text-sm ${tab === k ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">{k === 'recomendado' ? 'Recomendado' : 'Econômico'}</span>
                <span className="font-semibold text-slate-800">{formatCredits(k === 'recomendado' ? recTotal : economy.totalCredits, 3)} créditos</span>
              </button>
            ))}
          </div>
        )}
        {showing === 'economico' && economy
          ? <>
              {economy.rationale && <p className="mb-2 text-sm text-slate-600">{economy.rationale}</p>}
              {economy.loses && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800"><span className="font-semibold">O que perde:</span> {economy.loses}</p>}
            </>
          : p.rationale && <p className="mb-3 text-sm text-slate-600">{p.rationale}</p>}
        <BudgetLines lines={lines} />
        {budget && status === 'approved' && (
          <p className="mt-2 text-xs text-slate-500">Consumido até agora: <span className="font-semibold text-slate-700">{formatCredits(budget.consumedCredits, 3)}</span> de {formatCredits(budget.totalCredits, 3)} créditos{budget.approvedBy ? ` · aprovado por ${budget.approvedBy}` : ''}</p>
        )}
        <p className="mt-2 text-[11px] text-slate-400">Os tokens da conversa são cobrados pelo consumo real, fora deste orçamento.</p>
        {status === 'proposed' && canDecide && (
          <div className="mt-3 space-y-2">
            {rejecting && (
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                placeholder="O que mudar? (opcional)"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
              />
            )}
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!!busy} onClick={() => decide('approve', 'recomendado')} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50">
                {busy === 'approve' ? 'Aprovando…' : `${economy ? 'Aprovar recomendado' : 'Aprovar'} ${formatCredits(recTotal, 3)} créditos`}
              </button>
              {economy && (
                <button type="button" disabled={!!busy} onClick={() => decide('approve', 'economico')} className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                  {busy === 'economy' ? 'Aprovando…' : `Aprovar econômico ${formatCredits(economy.totalCredits, 3)}`}
                </button>
              )}
              {rejecting
                ? <button type="button" disabled={!!busy} onClick={() => decide('reject')} className="rounded-xl border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">{busy === 'reject' ? 'Enviando…' : 'Confirmar recusa'}</button>
                : <button type="button" disabled={!!busy} onClick={() => setRejecting(true)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Recusar / ajustar</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// "Anotei: …" — preferência salva na memória da marca, com desfazer por item. O desfazer apaga o
// que foi adicionado e desliga o que foi atualizado (o texto anterior não é guardado; o usuário
// ajusta na aba Memória da marca).
function MemoryCard({ p, onOpenMemory }: { p: any; onOpenMemory: () => void }) {
  const [undone, setUndone] = useState<Record<string, boolean>>({});
  const items: any[] = Array.isArray(p.items) ? p.items : [];
  if (!items.length) return null;
  const undo = async (it: any) => {
    try {
      if (it.action === 'add') await api.deleteCreativeMemory(it.id);
      else if (it.action === 'forget') await api.updateCreativeMemory(it.id, { active: true });
      else await api.updateCreativeMemory(it.id, { active: false });
      setUndone(prev => ({ ...prev, [it.id]: true }));
    } catch (e: any) { toast.error(e?.message || 'Não consegui desfazer'); }
  };
  return (
    <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 px-4 py-3 text-sm">
      <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">
        {p.source === 'inferred' ? 'Aprendi com esta conversa' : 'Anotei na memória da marca'}
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {items.map(it => (
          <li key={it.id} className="flex items-start gap-2">
            <span className={`min-w-0 flex-1 break-words ${undone[it.id] ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
              {it.action === 'forget' ? 'Não vale mais: ' : it.action === 'update' ? 'Atualizado: ' : ''}{it.content}
            </span>
            {undone[it.id]
              ? <span className="shrink-0 text-xs text-slate-400">desfeito</span>
              : <button type="button" onClick={() => undo(it)} className="shrink-0 text-xs font-semibold text-indigo-700 hover:underline">desfazer</button>}
          </li>
        ))}
      </ul>
      <button type="button" onClick={onOpenMemory} className="mt-2 text-xs font-semibold text-indigo-700 hover:underline">Ver e editar a memória da marca →</button>
    </div>
  );
}

export function CreativeTimeline({ events, budgets, status, onDecide, onDecidePublication, onImage, onOpenDeliverable, onProduceIdea, onOpenMemory }: {
  events: CreativeEvent[];
  budgets: CreativeBudgetRecord[];
  status: CreativeSessionStatus;
  onDecide: (budgetId: string, decision: 'approve' | 'reject', reason?: string, option?: 'recomendado' | 'economico') => Promise<void>;
  onImage: (url: string) => void;
  onOpenDeliverable: (id: string, index: number) => void;
  onDecidePublication: (publicationId: string, decision: 'approve' | 'reject', extra?: { caption?: string; tiktokCaption?: string; reason?: string; aiLabel?: boolean }) => Promise<void>;
  onProduceIdea: (idea: IdeaItem) => Promise<void>;
  onOpenMemory: () => void;
}) {
  const items = useMemo(() => groupEvents(events), [events]);
  const budgetById = useMemo(() => new Map(budgets.map(b => [b.id, b])), [budgets]);
  const lastIndex = items.length - 1;
  return (
    <div className="space-y-4">
      {items.map((item, idx) => {
        if (item.kind === 'work') {
          return <WorkBlock key={item.id} activities={item.activities} live={status === 'running' && idx === lastIndex} defaultOpen={idx >= lastIndex - 1} onImage={onImage} onOpenDeliverable={onOpenDeliverable} />;
        }
        const ev = item.event;
        const budget = ev.type === 'budget' ? budgetById.get((ev.payload || {}).budgetId) : undefined;
        return <TimelineEvent key={ev.id} ev={ev} budget={budget} status={status} onDecide={onDecide} onDecidePublication={onDecidePublication} onImage={onImage} onProduceIdea={onProduceIdea} onOpenMemory={onOpenMemory} />;
      })}
    </div>
  );
}

const TimelineEvent = memo(function TimelineEvent({ ev, budget, status, onDecide, onDecidePublication, onImage, onProduceIdea, onOpenMemory }: {
  ev: CreativeEvent;
  budget: CreativeBudgetRecord | undefined;
  status: CreativeSessionStatus;
  onDecide: (budgetId: string, decision: 'approve' | 'reject', reason?: string, option?: 'recomendado' | 'economico') => Promise<void>;
  onImage: (url: string) => void;
  onDecidePublication: (publicationId: string, decision: 'approve' | 'reject', extra?: { caption?: string; tiktokCaption?: string; reason?: string; aiLabel?: boolean }) => Promise<void>;
  onProduceIdea: (idea: IdeaItem) => Promise<void>;
  onOpenMemory: () => void;
}) {
        const p = ev.payload || {};
        switch (ev.type) {
          case 'user_message':
            return (
              <div key={ev.id} className="flex flex-col items-end gap-2">
                {Array.isArray(p.attachments) && p.attachments.length > 0 && (
                  <div className="flex max-w-[85%] flex-wrap justify-end gap-2">
                    {p.attachments.map((a: any) => a.kind === 'image'
                      ? (
                        <button key={a.path} type="button" onClick={() => onImage(a.url)} title={a.name} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                          <img src={a.url} alt={a.name} loading="lazy" decoding="async" className="h-28 w-28 object-cover transition group-hover:scale-105" />
                        </button>
                      )
                      : (
                        <a key={a.path} href={a.url} target="_blank" rel="noreferrer" title={a.name} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm hover:border-indigo-300">
                          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">{a.kind === 'video' ? 'vídeo' : a.kind === 'audio' ? 'áudio' : String(a.name || '').split('.').pop()?.slice(0, 4) || 'doc'}</span>
                          <span className="max-w-[180px] truncate">{a.name}</span>
                        </a>
                      ))}
                  </div>
                )}
                {p.text ? <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-slate-900 px-4 py-3 text-sm text-white shadow-sm">{p.text}</div> : null}
              </div>
            );
          case 'assistant_message':
            return (
              <div key={ev.id} className="flex gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-xs font-black text-white">DC</span>
                <div className="min-w-0 max-w-[85%] rounded-2xl rounded-tl-md border border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-sm">
                  <MarkdownText text={p.text} />
                </div>
              </div>
            );
          case 'plan':
            return <PlanCard key={ev.id} p={p} />;
          case 'ideas':
            return <IdeasCard key={ev.id} p={p} disabled={status === 'running' || status === 'queued'} onProduce={onProduceIdea} />;
          case 'agent_switch':
            return (
              <div key={ev.id} className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-slate-200" />
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${p.to === 'ideacao' ? 'bg-violet-50 text-violet-700' : 'bg-indigo-50 text-indigo-700'}`}>
                  {p.to === 'ideacao' ? 'Agente de Ideação assumiu' : 'Agente de Produção assumiu'}
                </span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
            );
          case 'budget':
            return <BudgetCard key={ev.id} p={p} budget={budget} canDecide={status === 'waiting_approval'} onDecide={onDecide} />;
          case 'publication':
            return <PublicationCard key={ev.id} p={p} canDecide={status === 'waiting_approval'} onDecide={onDecidePublication} />;
          case 'memory':
            return <MemoryCard key={ev.id} p={p} onOpenMemory={onOpenMemory} />;
          case 'notice':
            return <p key={ev.id} className="text-center text-xs font-medium text-slate-400">{p.message}</p>;
          case 'error':
            return (
              <div key={ev.id} className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {p.message}
                {p.detail && <p className="mt-1 text-xs text-rose-500">{p.detail}</p>}
              </div>
            );
          default:
            return null;
        }
});
